import React, { useCallback } from "react";
import { Upload, FileText, X, Loader2 } from "lucide-react";

interface ResumeUploaderProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  isParsing?: boolean;
  parseSuccess?: boolean;
}

const ResumeUploader: React.FC<ResumeUploaderProps> = ({
  file,
  onFileChange,
  isParsing = false,
  parseSuccess = false,
}) => {
  const validateAndSetFile = useCallback(
    (selectedFile: File) => {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const maxSize = 5 * 1024 * 1024;

      if (!allowedTypes.includes(selectedFile.type)) {
        alert("Please upload a PDF or Word document");
        return;
      }

      if (selectedFile.size > maxSize) {
        alert("File size must be less than 5MB");
        return;
      }

      onFileChange(selectedFile);
    },
    [onFileChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile) {
        validateAndSetFile(droppedFile);
      }
    },
    [validateAndSetFile]
  );

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const formatFileSize = (bytes: number) => {
    return (bytes / 1024 / 1024).toFixed(2) + " MB";
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Upload Resume <span className="text-gray-400">(Optional)</span>
        </label>
        {/* {!file && (
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-[#8B52FF]" />
            Auto-fills your info
          </span>
        )} */}
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className={`relative border-2 border-dashed rounded-xl transition-all ${
          file
            ? "border-[#8B52FF] bg-[#8B52FF]/5"
            : "border-gray-200 hover:border-[#8B52FF]/50 hover:bg-gray-50"
        }`}
      >
        <input
          type="file"
          id="resume-upload"
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.doc,.docx"
        />

        {file ? (
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#8B52FF]/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#8B52FF]" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm truncate max-w-[180px] sm:max-w-[280px]">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(file.size)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {isParsing && (
                  <div className="flex items-center gap-2 text-[#8B52FF] text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="hidden sm:inline">Parsing...</span>
                  </div>
                )}
                {parseSuccess && !isParsing && (
                  <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                    Form filled!
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onFileChange(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <label htmlFor="resume-upload" className="cursor-pointer block p-6">
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#8B52FF]/10 to-[#E05A6A]/10 flex items-center justify-center mb-3">
                <Upload className="w-6 h-6 text-[#8B52FF]" />
              </div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Drop your resume here or click to browse
              </p>
              <p className="text-xs text-gray-500">
                PDF or Word document (max 5MB)
              </p>
            </div>
          </label>
        )}
      </div>
    </div>
  );
};

export default ResumeUploader;
